import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import AddAdminIcon from "../../../components/icons/AddAdminIcon";
import AddEntityIcon from "../../../components/icons/AddEntityIcon";
import AddRoomInformationIcon from "../../../components/icons/AddRoomInformationIcon";
import AddRoomTimeIcon from "../../../components/icons/AddRoomTimeIcon";
import BanIcon from "../../../components/icons/BanIcon";
import DeleteActionIcon from "../../../components/icons/DeleteActionIcon";
import DeleteAdminIcon from "../../../components/icons/DeleteAdminIcon";
import EditAdminIcon from "../../../components/icons/EditAdminIcon";
import EditRoomInformationIcon from "../../../components/icons/EditRoomInformationIcon";
import EditRoomTimeIcon from "../../../components/icons/EditRoomTimeIcon";
import Loader from "../../../components/loader/Loader";
import NotificationIcon from "../../../components/icons/NotificationIcon";
import RefreshIcon from "../../../components/icons/RefreshIcon";
import RemoveEntityIcon from "../../../components/icons/RemoveEntityIcon";
import TransferIcon from "../../../components/icons/TransferIcon";
import ImageCreateIcon from "../../../components/icons/ImageCreateIcon";
import ImageDeleteIcon from "../../../components/icons/ImageDeleteIcon";
import SelectInput from "../../../components/inputs/selectInput/SelectInput";
import SettingsPagesContainer from "../../../components/settingsPagesContainer/SettingsPagesContainer";
import TextWrapper from "../../../components/textWrapper/TextWrapper";
import createActionIcon from "../../../assets/images/svgs/icons8-create-48.png";
import {
  fetchAdminActionAdmins,
  fetchAdminActionLogs,
  fetchAdminActionTournaments,
  fetchAdminActionTypes,
} from "../../../features/adminActionLogs/adminActionLogsSlice";
import { fetchAdminActionTargetDetailsAPI } from "../../../features/adminActionLogs/adminActionLogsAPI";
import styles from "./adminActionLogs.module.css";

const DEFAULT_LIMIT = 20;
const TOURNAMENT_FILTER_LIMIT = 250;

const STATUS_META = {
  success: {
    label: "ناجحة",
    className: "successStatus",
  },
  failed: {
    label: "فاشلة",
    className: "failedStatus",
  },
};

const AdminActionLogs = () => {
  const dispatch = useDispatch();
  const {
    items,
    pagination,
    fetchStatus,
    fetchActionTypesStatus,
    fetchActionAdminsStatus,
    fetchActionTournamentsStatus,
    actionTypes,
    admins,
    tournaments,
    error,
  } = useSelector((state) => state.adminActionLogs);

  const [page, setPage] = useState(1);
  const [draftFilters, setDraftFilters] = useState({
    actorAdminId: "",
    tournamentId: "",
    action: "",
    status: "",
  });
  const [filters, setFilters] = useState({
    actorAdminId: "",
    tournamentId: "",
    action: "",
    status: "",
  });
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [targetDetailsByKey, setTargetDetailsByKey] = useState({});
  const [targetDetailsStatusByKey, setTargetDetailsStatusByKey] = useState({});
  const tooltipPortalRef = useRef(null);

  useEffect(() => {
    if (fetchActionTypesStatus === "idle") {
      dispatch(fetchAdminActionTypes());
    }
  }, [dispatch, fetchActionTypesStatus]);

  useEffect(() => {
    if (fetchActionAdminsStatus === "idle") {
      dispatch(fetchAdminActionAdmins());
    }
  }, [dispatch, fetchActionAdminsStatus]);

  useEffect(() => {
    if (fetchActionTournamentsStatus === "idle") {
      dispatch(
        fetchAdminActionTournaments({
          page: 1,
          limit: TOURNAMENT_FILTER_LIMIT,
        }),
      );
    }
  }, [dispatch, fetchActionTournamentsStatus]);

  useEffect(() => {
    dispatch(
      fetchAdminActionLogs({
        page,
        limit: DEFAULT_LIMIT,
        ...filters,
      }),
    );
  }, [dispatch, page, filters]);

  useEffect(() => {
    const closeTooltip = () => setHoveredTooltip(null);
    window.addEventListener("scroll", closeTooltip, true);
    window.addEventListener("resize", closeTooltip);

    return () => {
      window.removeEventListener("scroll", closeTooltip, true);
      window.removeEventListener("resize", closeTooltip);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && isFiltersOpen) {
        setIsFiltersOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isFiltersOpen]);

  const handleApplyFilters = () => {
    setPage(1);
    setFilters({
      actorAdminId: draftFilters.actorAdminId.trim(),
      tournamentId: draftFilters.tournamentId.trim(),
      action: draftFilters.action.trim(),
      status: draftFilters.status.trim(),
    });
  };

  const handleClearFilters = () => {
    const cleared = {
      actorAdminId: "",
      tournamentId: "",
      action: "",
      status: "",
    };
    setDraftFilters(cleared);
    setFilters(cleared);
    setPage(1);
  };

  const actionNameByKey = useMemo(() => {
    return actionTypes.reduce((acc, item) => {
      if (item?.key) {
        acc[item.key] = item?.nameAr || item.key;
      }
      return acc;
    }, {});
  }, [actionTypes]);

  const getActionLabel = (actionKey) => {
    if (!actionKey) return "-";
    return actionNameByKey[actionKey] || actionKey;
  };

  const isCreateAction = (value) => {
    const normalized = String(value || "").toLowerCase();
    return (
      normalized.includes(".create") ||
      normalized.includes("create") ||
      normalized.includes("انشاء") ||
      normalized.includes("إنشاء")
    );
  };

  const isDeleteAction = (value) => {
    const normalized = String(value || "").toLowerCase();
    return (
      normalized.includes(".delete") ||
      normalized.includes("delete") ||
      normalized.includes("حذف")
    );
  };

  const adminNameById = useMemo(() => {
    return admins.reduce((acc, admin) => {
      if (admin?._id) {
        acc[admin._id] = admin?.name || admin._id;
      }
      return acc;
    }, {});
  }, [admins]);

  const tournamentItems = useMemo(
    () => (Array.isArray(tournaments?.items) ? tournaments.items : []),
    [tournaments],
  );

  const tournamentNameById = useMemo(() => {
    return tournamentItems.reduce((acc, tournament) => {
      if (tournament?._id) {
        acc[tournament._id] = tournament?.name || tournament._id;
      }
      return acc;
    }, {});
  }, [tournamentItems]);

  const appliedFilterItems = useMemo(() => {
    const list = [];

    if (filters.status) {
      list.push({
        key: "status",
        label: `الحالة: ${STATUS_META[filters.status]?.label || filters.status}`,
      });
    }

    if (filters.action) {
      list.push({
        key: "action",
        label: `العملية: ${getActionLabel(filters.action)}`,
      });
    }

    if (filters.actorAdminId) {
      list.push({
        key: "actorAdminId",
        label: `الادمن: ${adminNameById[filters.actorAdminId] || filters.actorAdminId}`,
      });
    }

    if (filters.tournamentId) {
      list.push({
        key: "tournamentId",
        label: `البطولة: ${
          tournamentNameById[filters.tournamentId] || filters.tournamentId
        }`,
      });
    }

    return list;
  }, [adminNameById, filters, getActionLabel, tournamentNameById]);

  const appliedFiltersCount = appliedFilterItems.length;

  const handleRemoveSingleFilter = (filterKey) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [filterKey]: "" }));
    setDraftFilters((prev) => ({ ...prev, [filterKey]: "" }));
  };

  const handleRefreshLogs = () => {
    dispatch(
      fetchAdminActionLogs({
        page,
        limit: DEFAULT_LIMIT,
        ...filters,
      }),
    );
  };

  const getTargetKey = (item) =>
    `${item?.action || ""}:${item?.targetId || ""}:${item?._id || ""}`;

  const formatTournamentType = (value) => {
    if (value === "solo") return "فردي";
    if (value === "squad") return "سكواد";
    return value || "-";
  };

  const formatGameName = (value) => {
    if (value === "freefire") return "فري فاير";
    if (value === "pubg") return "ببجي";
    return value || "-";
  };

  const formatEntryGameName = (entryGameName, tournamentGameName) => {
    if (entryGameName) return formatGameName(entryGameName);
    return formatGameName(tournamentGameName);
  };

  const renderNameWithId = (name, id) => {
    const normalizedName = name ? String(name) : "";
    const normalizedId = id ? String(id) : "";

    if (!normalizedName && !normalizedId) return "-";
    if (!normalizedName && normalizedId) {
      return <bdi className={styles.targetValueId}>{normalizedId}</bdi>;
    }

    return (
      <span className={styles.targetValueWithId} dir="rtl">
        <bdi className={styles.targetValueName}>{normalizedName}</bdi>
        {normalizedId ? (
          <bdi className={styles.targetValueId}>({normalizedId})</bdi>
        ) : null}
      </span>
    );
  };

  const renderListWithIds = (items = []) => {
    const normalized = (Array.isArray(items) ? items : [])
      .map((item) => ({
        name: item?.name ? String(item.name) : "",
        id: item?.id ? String(item.id) : "",
      }))
      .filter((item) => item.name || item.id);

    if (!normalized.length) return "-";

    return normalized.map((item, index) => (
      <span
        key={`${item.id || item.name || "item"}-${index}`}
        className={styles.targetValueListItem}
      >
        {renderNameWithId(item.name, item.id)}
        {index < normalized.length - 1 ? (
          <span className={styles.targetValueSeparator}>، </span>
        ) : null}
      </span>
    ));
  };

  const formatNameListWithIds = (items = []) => {
    return renderListWithIds(items);
  };

  const formatTableListWithIds = (tables = []) => {
    return renderListWithIds(tables);
  };

  const formatNotificationTargetType = (value) => {
    if (value === "all") return "الكل";
    if (value === "table") return "جدول";
    if (value === "tournament") return "بطولة";
    if (value === "user") return "لاعب";
    return value || "-";
  };

  const formatNotificationType = (value) => {
    if (value === "general") return "عام";
    if (value === "table") return "خاص بالجدول";
    if (value === "tournament") return "خاص بالبطولة";
    if (value === "table-full") return "امتلاء الجدول";
    if (value === "message") return "رسالة";
    if (value === "comment") return "تعليق";
    if (value === "support") return "دعم";
    return value || "-";
  };

  const formatNotificationBehaviorKind = (value) => {
    if (value === "general") return "عام";
    if (value === "open_link") return "فتح رابط";
    if (value === "scroll_to_tournament") return "الانتقال الى بطولة";
    if (value === "room_information") return "معلومات الروم";
    return value || "-";
  };

  const formatTableType = (value) => {
    if (value === "qualification") return "تصفيات";
    if (value === "nextStage") return "المرحلة المتقدمة";
    if (value === "final") return "النهائي";
    if (value === "winners") return "الفائزين";
    return value || "-";
  };

  const formatImageByteSize = (value) => {
    if (value === null || value === undefined) return "-";
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return "-";
    if (n < 1024) return `${Math.round(n)} بايت`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} ك.ب`;
    return `${(n / (1024 * 1024)).toFixed(2)} م.ب`;
  };

  const loadTargetDetails = async (item) => {
    const action = item?.action;
    const targetId = item?.targetId;
    const logId = item?._id;
    const targetKey = getTargetKey(item);

    if (!action || !targetId) return;
    if (
      targetDetailsByKey[targetKey] ||
      targetDetailsStatusByKey[targetKey] === "loading"
    ) {
      return;
    }

    setTargetDetailsStatusByKey((prev) => ({
      ...prev,
      [targetKey]: "loading",
    }));

    try {
      const response = await fetchAdminActionTargetDetailsAPI({
        action,
        targetId,
        logId,
      });
      setTargetDetailsByKey((prev) => ({
        ...prev,
        [targetKey]: response?.data || null,
      }));
      setTargetDetailsStatusByKey((prev) => ({
        ...prev,
        [targetKey]: "succeeded",
      }));
    } catch (error) {
      setTargetDetailsByKey((prev) => ({ ...prev, [targetKey]: null }));
      setTargetDetailsStatusByKey((prev) => ({
        ...prev,
        [targetKey]: "failed",
      }));
    }
  };

  const openTargetTooltip = (event, item, targetKey) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const estimatedTooltipHeight = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placeAbove =
      spaceBelow < estimatedTooltipHeight && spaceAbove > spaceBelow;

    const left = rect.left + rect.width / 2;
    const top = placeAbove ? rect.top - 10 : rect.bottom + 10;

    setHoveredTooltip({
      key: targetKey,
      action: item?.action || "",
      left,
      top,
      placeAbove,
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
    });
    loadTargetDetails(item);
  };

  const hoveredTargetKey = hoveredTooltip?.key || null;
  const hoveredTargetPayload = hoveredTargetKey
    ? targetDetailsByKey[hoveredTargetKey] || null
    : null;
  const hoveredTargetDetails = hoveredTargetPayload?.details || null;
  const hoveredTargetAction =
    hoveredTooltip?.action || hoveredTargetPayload?.action || null;
  const hoveredTargetDetailsStatus = hoveredTargetKey
    ? targetDetailsStatusByKey[hoveredTargetKey] || "idle"
    : "idle";

  useEffect(() => {
    if (!hoveredTooltip || !tooltipPortalRef.current) return;

    const viewportPadding = 8;
    const tooltipRect = tooltipPortalRef.current.getBoundingClientRect();
    const overflowsBottom =
      tooltipRect.bottom > window.innerHeight - viewportPadding;
    const overflowsTop = tooltipRect.top < viewportPadding;

    if (overflowsBottom && !hoveredTooltip.placeAbove) {
      setHoveredTooltip((prev) => {
        if (!prev || prev.key !== hoveredTooltip.key || prev.placeAbove)
          return prev;
        return {
          ...prev,
          placeAbove: true,
          top: (prev.anchorTop || tooltipRect.top) - 10,
        };
      });
      return;
    }

    if (overflowsTop && hoveredTooltip.placeAbove) {
      setHoveredTooltip((prev) => {
        if (!prev || prev.key !== hoveredTooltip.key || !prev.placeAbove)
          return prev;
        return {
          ...prev,
          placeAbove: false,
          top: (prev.anchorBottom || tooltipRect.bottom) + 10,
        };
      });
    }
  }, [hoveredTooltip, hoveredTargetDetails, hoveredTargetDetailsStatus]);

  return (
    <>
      <SettingsPagesContainer
        title="سجل عمليات المشرفين"
        fullWidth
        fullHeight
        displayContainerClassName={styles.logsDisplayContainer}
      >
        <div
          className={`${styles.container} ${
            isFiltersOpen ? styles.containerWithOpenDrawer : ""
          }`}
        >
          <div className={styles.toolbar}>
            <div className={styles.toolbarLead}>
              <button
                type="button"
                className={styles.refreshLogsButton}
                onClick={handleRefreshLogs}
                disabled={fetchStatus === "loading"}
                aria-label="تحديث سجل العمليات"
                title="تحديث"
              >
                <RefreshIcon className={styles.refreshLogsIcon} />
              </button>
              <button
                type="button"
                className={styles.filtersToggleButton}
                onClick={() => setIsFiltersOpen((prev) => !prev)}
              >
                {isFiltersOpen ? "إغلاق الفلاتر" : "إظهار الفلاتر"}
              </button>
              <div className={styles.filtersMeta}>
                الفلاتر المطبقة: {appliedFiltersCount}
              </div>
              <div className={styles.appliedFiltersList}>
                {appliedFilterItems.map((filterItem) => (
                  <span key={filterItem.key} className={styles.filterChip}>
                    <button
                      type="button"
                      className={styles.filterChipRemoveButton}
                      onClick={() => handleRemoveSingleFilter(filterItem.key)}
                      aria-label={`إزالة فلتر ${filterItem.label}`}
                    >
                      ×
                    </button>
                    <span>{filterItem.label}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.resultsArea}>
            {fetchStatus === "loading" && items.length === 0 ? (
              <div className={styles.loaderContainer}>
                <Loader />
              </div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>الوقت</th>
                      <th>الادمن</th>
                      <th>المنصب</th>
                      <th>العملية</th>
                      <th>الحالة</th>
                      <th>الهدف</th>
                      <th>المسار</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const statusMeta = STATUS_META[item.status] || {
                        label: item.status || "-",
                        className: "",
                      };
                      const actionLabel = getActionLabel(item.action);
                      const isAddAdminAction = item.action === "admin.create";
                      const isEditAdminAction = item.action === "admin.update";
                      const isDeleteAdminAction =
                        item.action === "admin.delete";
                      const isAddEntityAction =
                        item.action === "table.add-entry";
                      const isTableEntryRemovalAction =
                        item.action === "table.remove-entry";
                      const isAddRoomInformationAction =
                        item.action === "table.room-information.create";
                      const isEditRoomInformationAction =
                        item.action === "table.room-information.update";
                      const isAddRoomTimeAction =
                        item.action === "table.room-time.create";
                      const isEditRoomTimeAction =
                        item.action === "table.room-time.update";
                      const isDeletionAction =
                        !isTableEntryRemovalAction &&
                        !isDeleteAdminAction &&
                        (isDeleteAction(item.action) ||
                          isDeleteAction(actionLabel));
                      const isBanAction = ["user.ban", "user.unban"].includes(
                        item.action,
                      );
                      const isNotificationAction =
                        item.action === "notification.send";
                      const isMoveEntryAction =
                        item.action === "table.move-entry";
                      const isImageCreateAction = item.action === "image.create";
                      const isImageDeleteAction = item.action === "image.delete";
                      const OperationIcon = isImageDeleteAction
                        ? ImageDeleteIcon
                        : isImageCreateAction
                          ? ImageCreateIcon
                          : isDeleteAdminAction
                        ? DeleteAdminIcon
                        : isDeletionAction
                        ? DeleteActionIcon
                        : isAddAdminAction
                        ? AddAdminIcon
                        : isEditAdminAction
                        ? EditAdminIcon
                        : isAddRoomInformationAction
                        ? AddRoomInformationIcon
                        : isEditRoomInformationAction
                        ? EditRoomInformationIcon
                        : isAddRoomTimeAction
                        ? AddRoomTimeIcon
                        : isEditRoomTimeAction
                        ? EditRoomTimeIcon
                        : isAddEntityAction
                        ? AddEntityIcon
                        : isTableEntryRemovalAction
                        ? RemoveEntityIcon
                        : isBanAction
                        ? BanIcon
                        : isNotificationAction
                        ? NotificationIcon
                        : isMoveEntryAction
                          ? TransferIcon
                          : null;
                      const operationToneClassName = isImageDeleteAction
                        ? styles.imageDeleteOperationIcon
                        : isImageCreateAction
                          ? styles.imageCreateOperationIcon
                          : isDeleteAdminAction
                        ? styles.deleteAdminOperationIcon
                        : isDeletionAction
                        ? styles.deleteOperationIcon
                        : isAddAdminAction
                        ? styles.addAdminOperationIcon
                        : isEditAdminAction
                        ? styles.editAdminOperationIcon
                        : isAddRoomInformationAction
                        ? styles.addRoomInformationOperationIcon
                        : isEditRoomInformationAction
                        ? styles.editRoomInformationOperationIcon
                        : isAddRoomTimeAction
                        ? styles.addRoomTimeOperationIcon
                        : isEditRoomTimeAction
                        ? styles.editRoomTimeOperationIcon
                        : isAddEntityAction
                        ? styles.addEntryOperationIcon
                        : isTableEntryRemovalAction
                        ? styles.removeEntryOperationIcon
                        : isBanAction
                        ? styles.banOperationIcon
                        : isNotificationAction
                        ? styles.notificationOperationIcon
                        : isMoveEntryAction
                          ? styles.moveEntryOperationIcon
                          : "";
                      const operationIconSrc =
                        !isImageCreateAction &&
                        !isImageDeleteAction &&
                        (isCreateAction(item.action) ||
                          isCreateAction(actionLabel))
                          ? createActionIcon
                          : null;
                      const targetKey = getTargetKey(item);

                      return (
                        <tr key={item._id}>
                          <td>
                            {new Date(item.createdAt).toLocaleString("ar-EG")}
                          </td>
                          <td>{item.actorName || "-"}</td>
                          <td>{item.actorRole || "-"}</td>
                          <td>
                            <span
                              className={`${styles.operationCell} ${operationToneClassName}`.trim()}
                            >
                              {OperationIcon ? (
                                <OperationIcon className={styles.operationIcon} />
                              ) : operationIconSrc ? (
                                <img
                                  src={operationIconSrc}
                                  alt="operation-action"
                                  className={styles.operationIcon}
                                />
                              ) : null}
                              <span>{actionLabel}</span>
                            </span>
                          </td>
                          <td>
                            <span
                              className={`${styles.statusBadge} ${styles[statusMeta.className] || ""}`}
                            >
                              {statusMeta.label}
                            </span>
                          </td>
                          <td>
                            <div className={styles.targetCell}>
                              <span>
                                {renderNameWithId(
                                  item.targetType,
                                  item.targetId,
                                )}
                              </span>
                              <span
                                className={styles.targetInfoWrapper}
                                onMouseEnter={(event) => {
                                  openTargetTooltip(event, item, targetKey);
                                }}
                                onMouseLeave={() => {
                                  setHoveredTooltip((prev) =>
                                    prev?.key === targetKey ? null : prev,
                                  );
                                }}
                              >
                                <span
                                  className={styles.targetInfoMark}
                                  aria-label="تفاصيل الهدف"
                                >
                                  !
                                </span>
                              </span>
                            </div>
                          </td>
                          <td>{item?.metadata?.path || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {fetchStatus !== "loading" && items.length === 0 && (
                  <div className={styles.emptyState}>
                    <TextWrapper textStyle="textSmall">
                      لا توجد نتائج حالياً
                    </TextWrapper>
                  </div>
                )}
              </div>
            )}

            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.button}
                disabled={pagination.page <= 1 || fetchStatus === "loading"}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                السابق
              </button>
              <TextWrapper textStyle="textXSmall">
                صفحة {pagination.page || 1} من {pagination.totalPages || 1}{" "}
                (إجمالي: {pagination.total || 0})
              </TextWrapper>
              <button
                type="button"
                className={styles.button}
                disabled={!pagination.hasMore || fetchStatus === "loading"}
                onClick={() => setPage((prev) => prev + 1)}
              >
                التالي
              </button>
            </div>
          </div>

          {error && (
            <div className={styles.errorText}>
              <TextWrapper textStyle="textSmall">{error}</TextWrapper>
            </div>
          )}
        </div>
      </SettingsPagesContainer>

      {isFiltersOpen &&
        createPortal(
          <aside className={styles.filterDrawerPanel}>
            <div className={styles.sidebarHeader}>
              <h3 className={styles.sidebarTitle}>الفلاتر</h3>
              <button
                type="button"
                className={styles.sidebarCloseButton}
                onClick={() => setIsFiltersOpen(false)}
                aria-label="اغلاق الفلاتر"
              >
                ×
              </button>
            </div>

            <div className={styles.sidebarActions}>
              <button
                type="button"
                className={`${styles.button} ${styles.filterApplyButton}`}
                onClick={handleApplyFilters}
              >
                تطبيق
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.secondaryButton} ${styles.filterClearButton}`}
                onClick={handleClearFilters}
              >
                مسح
              </button>
            </div>

            <div className={styles.sidebarBody}>
              <div className={styles.sidebarField}>
                <label className={styles.sidebarLabel}>الحالة</label>
                <SelectInput
                  className={`${styles.input} ${styles.sidebarSelect}`}
                  value={draftFilters.status}
                  setValue={(nextValue) =>
                    setDraftFilters((prev) => ({ ...prev, status: nextValue }))
                  }
                  placeHolder="كل الحالات"
                  searchable
                  searchThreshold={0}
                  searchPlaceholder="ابحث عن حالة"
                  emptyResultsLabel="لا توجد حالات مطابقة"
                >
                  <option value="success">ناجحة</option>
                  <option value="failed">فاشلة</option>
                </SelectInput>
              </div>

              <div className={styles.sidebarField}>
                <label className={styles.sidebarLabel}>العملية</label>
                <SelectInput
                  className={`${styles.input} ${styles.sidebarSelect}`}
                  value={draftFilters.action}
                  setValue={(nextValue) =>
                    setDraftFilters((prev) => ({ ...prev, action: nextValue }))
                  }
                  placeHolder="كل العمليات"
                  searchable
                  searchThreshold={0}
                  searchPlaceholder="ابحث عن عملية"
                  emptyResultsLabel="لا توجد عمليات مطابقة"
                  disabled={fetchActionTypesStatus === "loading"}
                >
                  {actionTypes.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.nameAr}
                    </option>
                  ))}
                </SelectInput>
              </div>

              <div className={styles.sidebarField}>
                <label className={styles.sidebarLabel}>الادمن</label>
                <SelectInput
                  className={`${styles.input} ${styles.sidebarSelect}`}
                  value={draftFilters.actorAdminId}
                  setValue={(nextValue) =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      actorAdminId: nextValue,
                    }))
                  }
                  placeHolder="كل الادمن"
                  searchable
                  searchThreshold={0}
                  searchPlaceholder="ابحث عن ادمن"
                  emptyResultsLabel="لا توجد أسماء مطابقة"
                  disabled={fetchActionAdminsStatus === "loading"}
                >
                  {admins.map((admin) => (
                    <option key={admin._id} value={admin._id}>
                      {admin.name}
                      {admin.role ? ` (${admin.role})` : ""}
                    </option>
                  ))}
                </SelectInput>
              </div>

              <div className={styles.sidebarField}>
                <label className={styles.sidebarLabel}>البطولة</label>
                <SelectInput
                  className={`${styles.input} ${styles.sidebarSelect}`}
                  value={draftFilters.tournamentId}
                  setValue={(nextValue) =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      tournamentId: nextValue,
                    }))
                  }
                  placeHolder="كل البطولات"
                  searchable
                  searchThreshold={0}
                  searchPlaceholder="ابحث عن بطولة"
                  emptyResultsLabel="لا توجد بطولات مطابقة"
                  disabled={
                    fetchActionTournamentsStatus === "loading" &&
                    tournamentItems.length === 0
                  }
                >
                  {tournamentItems.map((tournament) => (
                    <option key={tournament._id} value={tournament._id}>
                      {tournament.name || tournament._id}
                    </option>
                  ))}
                </SelectInput>
              </div>
            </div>
          </aside>,
          document.body,
        )}

      {hoveredTooltip &&
        createPortal(
          <div
            ref={tooltipPortalRef}
            className={`${styles.targetTooltipPortal} ${
              hoveredTooltip.placeAbove
                ? styles.tooltipAbove
                : styles.tooltipBelow
            }`}
            style={{
              left: hoveredTooltip.left,
              top: hoveredTooltip.top,
              transform: hoveredTooltip.placeAbove
                ? "translate(-50%, -100%)"
                : "translate(-50%, 0)",
            }}
          >
            {hoveredTargetDetailsStatus === "loading" && (
              <div>جاري تحميل التفاصيل...</div>
            )}
            {hoveredTargetDetailsStatus !== "loading" &&
              hoveredTargetDetails && (
                <>
                  {hoveredTargetAction === "table.create" ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.name || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نوع الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatTableType(hoveredTargetDetails.tableType)}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اللاعبون:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.playersCount ?? "-"} /{" "}
                          {hoveredTargetDetails.playersLimit ?? "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.tournamentName,
                            hoveredTargetDetails.tournamentId,
                          )}
                        </bdi>
                      </div>
                    </>
                  ) : hoveredTargetAction === "notification.send" ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          هدف الاشعار:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatNotificationTargetType(
                            hoveredTargetDetails.targetType,
                          )}
                          {hoveredTargetDetails.targetName ||
                          hoveredTargetDetails.targetId
                            ? " - "
                            : ""}
                          {hoveredTargetDetails.targetName ||
                          hoveredTargetDetails.targetId
                            ? renderNameWithId(
                                hoveredTargetDetails.targetName,
                                hoveredTargetDetails.targetId,
                              )
                            : null}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          العنوان:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.title || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          المحتوى:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.body || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          وقت الارسال:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.sentAt
                            ? new Date(
                                hoveredTargetDetails.sentAt,
                              ).toLocaleString("ar-EG")
                            : "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نوع الاشعار:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatNotificationType(
                            hoveredTargetDetails.notificationType,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          سلوك الاشعار:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatNotificationBehaviorKind(
                            hoveredTargetDetails.behaviorKind,
                          )}
                        </bdi>
                      </div>
                      {hoveredTargetDetails.behaviorKind === "open_link" && (
                        <div className={styles.targetTooltipRow}>
                          <strong className={styles.targetTooltipLabel}>
                            الرابط:
                          </strong>
                          <bdi className={styles.targetTooltipValue}>
                            {hoveredTargetDetails.behaviorDetails?.link || "-"}
                          </bdi>
                        </div>
                      )}
                      {hoveredTargetDetails.behaviorKind ===
                        "scroll_to_tournament" && (
                        <div className={styles.targetTooltipRow}>
                          <strong className={styles.targetTooltipLabel}>
                            البطولة:
                          </strong>
                          <bdi className={styles.targetTooltipValue}>
                            {renderNameWithId(
                              hoveredTargetDetails.behaviorDetails
                                ?.tournamentName,
                              hoveredTargetDetails.behaviorDetails
                                ?.tournamentId,
                            )}
                          </bdi>
                        </div>
                      )}
                      {hoveredTargetDetails.behaviorKind ===
                        "room_information" && (
                        <>
                          <div className={styles.targetTooltipRow}>
                            <strong className={styles.targetTooltipLabel}>
                              اسم الروم:
                            </strong>
                            <bdi className={styles.targetTooltipValue}>
                              {hoveredTargetDetails.behaviorDetails?.roomName ||
                                "-"}
                            </bdi>
                          </div>
                          <div className={styles.targetTooltipRow}>
                            <strong className={styles.targetTooltipLabel}>
                              ايدي الروم:
                            </strong>
                            <bdi className={styles.targetTooltipValue}>
                              {hoveredTargetDetails.behaviorDetails?.roomId ||
                                "-"}
                            </bdi>
                          </div>
                          <div className={styles.targetTooltipRow}>
                            <strong className={styles.targetTooltipLabel}>
                              باسوورد الروم:
                            </strong>
                            <bdi className={styles.targetTooltipValue}>
                              {hoveredTargetDetails.behaviorDetails?.roomPass ||
                                "-"}
                            </bdi>
                          </div>
                          <div className={styles.targetTooltipRow}>
                            <strong className={styles.targetTooltipLabel}>
                              بطولة الروم:
                            </strong>
                            <bdi className={styles.targetTooltipValue}>
                              {renderNameWithId(
                                hoveredTargetDetails.behaviorDetails
                                  ?.tournamentName,
                                hoveredTargetDetails.behaviorDetails
                                  ?.tournamentId,
                              )}
                            </bdi>
                          </div>
                        </>
                      )}
                    </>
                  ) : ["user.ban", "user.unban"].includes(
                      hoveredTargetAction,
                    ) ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم المستخدم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.name,
                            hoveredTargetDetails.userId,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          الحالة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetAction === "user.ban"
                            ? "تم حظره"
                            : "تم إلغاء حظره"}
                        </bdi>
                      </div>
                      {hoveredTargetDetails.banReason ? (
                        <div className={styles.targetTooltipRow}>
                          <strong className={styles.targetTooltipLabel}>
                            سبب الحظر:
                          </strong>
                          <bdi className={styles.targetTooltipValue}>
                            {hoveredTargetDetails.banReason}
                          </bdi>
                        </div>
                      ) : null}
                    </>
                  ) : hoveredTargetAction === "user.delete" ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم المستخدم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.name,
                            hoveredTargetDetails.userId,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          تاريخ إنشاء الحساب:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.accountCreatedAt
                            ? new Date(
                                hoveredTargetDetails.accountCreatedAt,
                              ).toLocaleString("ar-EG")
                            : "-"}
                        </bdi>
                      </div>
                    </>
                  ) : ["image.create", "image.delete"].includes(
                      hoveredTargetAction,
                    ) ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          الاسم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.name || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          الوسم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.tag || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          النوع:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.kind || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نوع الملف:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.mimeType || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          الحجم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatImageByteSize(hoveredTargetDetails.size)}
                        </bdi>
                      </div>
                    </>
                  ) : ["admin.create", "admin.delete"].includes(
                      hoveredTargetAction,
                    ) ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم الأدمن:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.name,
                            hoveredTargetDetails.adminId,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          الدور:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.role || "-"}
                        </bdi>
                      </div>
                    </>
                  ) : [
                      "table.room-information.create",
                      "table.room-information.update",
                    ].includes(hoveredTargetAction) ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.tableName || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نوع الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatTableType(hoveredTargetDetails.tableType)}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.tournamentName,
                            hoveredTargetDetails.tournamentId,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم الروم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.roomName || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          ايدي الروم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.roomId || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          باسوورد الروم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.roomPass || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نص الرسالة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.body || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          تم ارسال اشعار:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.notificationSent === null
                            ? "-"
                            : hoveredTargetDetails.notificationSent
                              ? "نعم"
                              : "لا"}
                        </bdi>
                      </div>
                    </>
                  ) : [
                      "table.room-time.create",
                      "table.room-time.update",
                    ].includes(hoveredTargetAction) ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.tableName || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نوع الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatTableType(hoveredTargetDetails.tableType)}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.tournamentName,
                            hoveredTargetDetails.tournamentId,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          موعد الروم:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.eventAtUtc
                            ? new Date(
                                hoveredTargetDetails.eventAtUtc,
                              ).toLocaleString("ar-EG")
                            : "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          تم ارسال اشعار:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.notificationSent === null
                            ? "-"
                            : hoveredTargetDetails.notificationSent
                              ? "نعم"
                              : "لا"}
                        </bdi>
                      </div>
                    </>
                  ) : hoveredTargetAction === "table.delete" ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.name || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نوع الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatTableType(hoveredTargetDetails.tableType)}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          عدد اللاعبين عند الحذف:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.playersCountAtDelete ?? "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.tournamentName,
                            hoveredTargetDetails.tournamentId,
                          )}
                        </bdi>
                      </div>
                    </>
                  ) : hoveredTargetAction === "table.add-entry" ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          {hoveredTargetDetails.kind === "squad"
                            ? "اسم السكواد:"
                            : "اسم اللاعب:"}
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.name,
                            hoveredTargetDetails.entryId,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          معرف اللعبة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.gameId || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم اللعبة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatEntryGameName(
                            hoveredTargetDetails.gameName,
                            hoveredTargetDetails.tournamentGameName,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.tableName || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.tournamentName,
                            hoveredTargetDetails.tournamentId,
                          )}
                        </bdi>
                      </div>
                    </>
                  ) : hoveredTargetAction === "table.move-entry" ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.tournamentName,
                            hoveredTargetDetails.tournamentId,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          المنقول من:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatTableListWithIds(
                            hoveredTargetDetails.fromTables,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          المنقول إلى:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatTableListWithIds([
                            {
                              id: hoveredTargetDetails.toTableId,
                              name: hoveredTargetDetails.toTableName,
                            },
                          ])}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          قائمة اللاعبين:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatNameListWithIds(
                            hoveredTargetDetails.movedPlayers,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          قائمة السكوادات:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatNameListWithIds(
                            hoveredTargetDetails.movedSquads,
                          )}
                        </bdi>
                      </div>
                    </>
                  ) : hoveredTargetAction === "table.remove-entry" ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          {hoveredTargetDetails.kind === "squad"
                            ? "اسم السكواد:"
                            : "اسم اللاعب:"}
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.name,
                            hoveredTargetDetails.entryId,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          معرف اللعبة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.gameId || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم اللعبة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatEntryGameName(
                            hoveredTargetDetails.gameName,
                            hoveredTargetDetails.tournamentGameName,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.tableName || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {renderNameWithId(
                            hoveredTargetDetails.tournamentName,
                            hoveredTargetDetails.tournamentId,
                          )}
                        </bdi>
                      </div>
                    </>
                  ) : hoveredTargetAction === "table.create-next-stage" ? (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.name || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نوع البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatTournamentType(
                            hoveredTargetDetails.tournamentType,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اللعبة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatGameName(hoveredTargetDetails.gameName)}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          عدد الجداول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.nextStageTablesCount ?? "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          حجم الجدول:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.nextStageTableSize ?? "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          الجائزة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.prize ?? "-"}
                        </bdi>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اسم البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.name || "-"}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          نوع البطولة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatTournamentType(
                            hoveredTargetDetails.tournamentType,
                          )}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          اللعبة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {formatGameName(hoveredTargetDetails.gameName)}
                        </bdi>
                      </div>
                      <div className={styles.targetTooltipRow}>
                        <strong className={styles.targetTooltipLabel}>
                          الجائزة:
                        </strong>
                        <bdi className={styles.targetTooltipValue}>
                          {hoveredTargetDetails.prize ?? "-"}
                        </bdi>
                      </div>
                    </>
                  )}
                </>
              )}
            {hoveredTargetDetailsStatus !== "loading" &&
              !hoveredTargetDetails && <div>لا توجد تفاصيل متاحة</div>}
          </div>,
          document.body,
        )}
    </>
  );
};

export default AdminActionLogs;
